import { Button } from '@capsule/consumer-design-system';

import classNames from 'classnames';
import { push } from 'connected-react-router';
import map from 'lodash/map';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { AnalyticSources } from 'src/app/analytics/analyticsEvents';
import useUnreadChatMessages from 'src/app/components/CapsuleChat/hooks/useUnreadChatMessages';
import LinkToCardItem from 'src/app/components/Elements/Card/LinkToCardItem';
import { RescheduleOrderStepName } from 'src/app/components/Pages/Order/FailedOrderReschedule/types';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { orderTracking, rescheduleDeliveryWindows, rescheduleOrder } from 'src/app/router/paths';
import { openChat } from 'src/app/state/slices/chat';
import type { Order } from 'src/app/types';
import { CTATypes, ORDER_STATUSES } from 'src/consts';

import { CTATypesToTextMap } from '../../Medication/utils';
import { getCombinedOrderItemNames } from '../utils';

import { PostponedSubtitleText, SubtitleText } from './styles';
import styles from './styles.module.scss';

interface ActiveOrderProps {
  order: Order;
  index: number;
}

const ButtonContainer = styled.div`
  padding-top: ${({ theme }) => theme.space.s5};
  background-color: ${({ theme }) => theme.colors.white};
`;

export const ActiveOrder = ({ order, index }: ActiveOrderProps) => {
  const dispatch = useDispatch();
  const itemNames = map(order?.items, 'name');
  const combinedItemNames = getCombinedOrderItemNames(itemNames);
  const orderRescheduleType: CTATypes | undefined = order?.reschedule?.cta?.action?.type;

  const dispatchSelectRescheduleSegementEvent = useSegmentEvent('selectReschedule', { shouldTrackOnMount: false });
  const dispatchOpenChatSegementEvent = useSegmentEvent('openChat', { shouldTrackOnMount: false });
  const { hasUnreadMessages } = useUnreadChatMessages();
  const handleOnRescheduleClicked = () => {
    if (!order) return;

    if (orderRescheduleType === CTATypes.rescheduleOrder) {
      dispatch(push(rescheduleDeliveryWindows.toPath({ orderId: order.id })));
    } else if (orderRescheduleType === CTATypes.scheduleDelivery) {
      dispatch(push(rescheduleDeliveryWindows.toPath({ orderId: order.id })));
    } else if (orderRescheduleType === CTATypes.openChat) {
      dispatchSelectRescheduleSegementEvent({ source: AnalyticSources.orders });
      dispatchOpenChatSegementEvent({
        source: AnalyticSources.rescheduleDeliveryOrders,
        has_unread_message: hasUnreadMessages,
      });
      dispatch(
        openChat({
          prepopulatedMessage: `Hi Capsule, I need to reschedule my delivery for ${combinedItemNames}.`,
          source: AnalyticSources.rescheduleDeliveryOrders,
        })
      );
    } else if (orderRescheduleType === CTATypes.rescheduleOrderInvalidAddress) {
      dispatch(
        push(
          rescheduleOrder.toPath({
            orderId: order.id,
            stepName: RescheduleOrderStepName.Initial,
          })
        )
      );
    }
  };

  if (!order) return null;

  const orderStatusText = order.statusText;
  const isOrderPostponed = order?.status === ORDER_STATUSES.postponed;
  const ctaType = order?.reschedule?.cta?.action?.type;
  const rescheduleTitle = order?.reschedule?.title;
  const rescheduleDescription = order?.reschedule?.description;
  return (
    <LinkToCardItem
      data-cy={`new-active-order-link-${index}`}
      key={order?.id}
      to={orderTracking.toPath({ orderId: order?.id })}
      showArrow={true}
      noMaxWidth={isOrderPostponed}
      aria-label={`${orderStatusText} for ${combinedItemNames}`}
    >
      <div className={classNames(styles.orderLink, 'inspectlet-sensitive')}>
        <h3 className={classNames('pb-0', styles.overflowEllipsis)}>
          {isOrderPostponed && rescheduleTitle ? rescheduleTitle : orderStatusText}
        </h3>
        {isOrderPostponed ? (
          <PostponedSubtitleText variant="body">{rescheduleDescription}</PostponedSubtitleText>
        ) : (
          <SubtitleText variant="body">{order?.deliveryMethod?.flavorText}</SubtitleText>
        )}
        {map(itemNames, (itemName, index) => (
          <span key={`${itemName}_${index}`} className={classNames(styles.orderItem, 'normal')}>
            {itemName}
          </span>
        ))}
      </div>
      {isOrderPostponed && (
        <ButtonContainer>
          <Button
            variant="contained-secondary-lg"
            isFullWidth
            onClick={event => {
              // event.stopPropagation doesn't seem to prevent event bubbling enough here, so we preventDefault
              event.preventDefault();
              handleOnRescheduleClicked();
            }}
          >
            {ctaType && ctaType in CTATypesToTextMap ? CTATypesToTextMap[ctaType] : 'Reschedule Order'}
          </Button>
        </ButtonContainer>
      )}
    </LinkToCardItem>
  );
};
